/**
 * LocalTime — render an ISO timestamp as the visitor's local date/time, SSR-safe.
 *
 * Formatting is driven by the shared request preferences (locale + timezone) resolved
 * once at the request boundary (see `lib/preferences.ts` + `PreferencesProvider`), so the
 * server and the client render the SAME string — no hydration mismatch, no
 * `suppressHydrationWarning`, no per-component mount guard. On repeat visits the server
 * already knows the visitor's exact timezone (from the cookie the reconciler wrote), so
 * the correct local time is rendered server-side with no client correction at all.
 *
 * Use <LocalTime> when the timestamp renders on its own (a table cell, a caption).
 * Use useHydratedTime() when the value is interpolated into copy (a sentence, a title).
 */
import { type ReactElement } from 'react';
import { useFormatDateTime } from '~/lib/preferences-context';
import type { DateVariant } from '~/lib/preferences';

export type LocalTimeVariant = DateVariant;

/** The visitor's local date/time for an ISO timestamp — for interpolation into copy. */
export function useHydratedTime(iso: string, variant: LocalTimeVariant = 'datetime'): string {
  return useFormatDateTime()(iso, variant);
}

export interface LocalTimeProps {
  /** ISO 8601 timestamp. */
  readonly iso: string;
  /** `datetime` (date + time of day, the default) or `date` (date only). */
  readonly variant?: LocalTimeVariant;
  readonly className?: string;
}

/**
 * Render an ISO timestamp as the visitor's local date/time in a semantic `<time>`. The
 * machine-readable `dateTime` carries the raw ISO for assistive tech and styling hooks.
 */
export function LocalTime({ iso, variant = 'datetime', className }: LocalTimeProps): ReactElement {
  const format = useFormatDateTime();
  return (
    <time dateTime={iso} className={className}>
      {format(iso, variant)}
    </time>
  );
}
